import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AppProvider,
    Page,
    Layout,
    Card,
    Text,
    BlockStack,
    InlineStack,
    Badge,
    Button,
    Link,
    Banner,
    Spinner,
    Box,
    Modal,
    Divider,
    EmptyState,
    ProgressBar,
    List,
    TextField,
    Select,
    Icon,
} from "@shopify/polaris";
import { EmailIcon } from "@shopify/polaris-icons";
import enTranslations from "@shopify/polaris/locales/en.json";
import frTranslations from "@shopify/polaris/locales/fr.json";
import PlanPicker from "./PlanPicker";

type AppStatus = 'loading' | 'setting_up' | 'connected' | 'syncing' | 'error' | 'not_connected';

interface ConnectedAccount {
    channelId?: string;
    channelName?: string;
    userId: string;
    userEmail?: string;
    userName?: string;
    lastSyncAt?: string;
    status: string;
    isConnected: boolean;
    syncDetails?: {
        commercialMessage?: string;
        commercialAction?: string;
        isRetryable?: boolean;
        errorCode?: number;
    } | null;
}

interface ConnectionState {
    status: AppStatus;
    connections: ConnectedAccount[];
    shopName?: string;
    error?: string;
    accountEmail?: string;
    shopEmail?: string;
    setupEmailExists?: boolean;
}

// Extract shop synchronously to avoid an extra render cycle
function getInitialShop(): string {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("shop") || window.shopify?.config?.shop || "";
}

// Wait for App Bridge to be ready and able to produce a valid session token.
// Without this, API calls fire before App Bridge initializes, causing the server
// to return 403 + X-Shopify-API-Request-Failure-Reauthorize headers, which
// App Bridge intercepts and triggers a full-page redirect (the visible "refresh").
function waitForAppBridge(): Promise<void> {
    return new Promise((resolve) => {
        const tryResolve = (attempts = 0) => {
            if (window.shopify?.idToken) {
                // Verify we can actually get a token (not just that the function exists)
                window.shopify.idToken()
                    .then(() => resolve())
                    .catch(() => {
                        if (attempts < 20) setTimeout(() => tryResolve(attempts + 1), 150);
                        else resolve(); // Give up waiting, let normal error handling take over
                    });
            } else if (attempts < 20) {
                setTimeout(() => tryResolve(attempts + 1), 150);
            } else {
                resolve(); // Give up after ~3 seconds
            }
        };
        tryResolve();
    });
}

export default function App() {
    const { t, i18n } = useTranslation();
    const [shop, setShop] = useState<string>(getInitialShop);
    const [appBridgeReady, setAppBridgeReady] = useState(false);
    const [connectionState, setConnectionState] = useState<ConnectionState>({
        status: 'loading',
        connections: []
    });
    const [isRetryingSync, setIsRetryingSync] = useState(false);
    const [isSettingUp, setIsSettingUp] = useState(false);
    const [showDisconnectModal, setShowDisconnectModal] = useState(false);
    const [disconnectTarget, setDisconnectTarget] = useState<ConnectedAccount | null>(null);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [setupError, setSetupError] = useState<string | null>(null);
    const [loginEmail, setLoginEmail] = useState('');
    const [loginOtp, setLoginOtp] = useState('');
    const [isWaitingForOtp, setIsWaitingForOtp] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    // True when the entered email has no Dashbal account yet — the OTP flow
    // will create one on verification, so we show create-account copy.
    const [willCreateAccount, setWillCreateAccount] = useState(false);
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginEmail);

    // Prefill email with shop email when not connected and email is empty
    useEffect(() => {
        if (connectionState.status === 'not_connected' && connectionState.shopEmail && !loginEmail) {
            setLoginEmail(connectionState.shopEmail);
        }
    }, [connectionState.status, connectionState.shopEmail]);

    const hasInitialCheckRun = useRef(false);
    const hasAutoSetupRun = useRef(false);
    const settingUpSince = useRef<number | null>(null);

    const config = window.__DASHBAL_CONFIG__ || {
        dashbalUrl: "https://dashbal.com",
        docsUrl: "https://dashbal.com/docs",
        supportEmail: "support@dashbal.com",
        manageIntegrationUrl: "",
        contactSupportUrl: "",
        apiKey: "",
        shopifyAppHandle: "",
    };

    const { dashbalUrl, docsUrl, supportEmail, contactSupportUrl } = config;

    // Get session token for authenticated requests
    const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
        const headers: Record<string, string> = {};
        if (window.shopify?.idToken) {
            try {
                const sessionToken = await window.shopify.idToken();
                headers['Authorization'] = `Bearer ${sessionToken}`;
            } catch (e) {
                // Fallback for older App Bridge
                if (window.shopify?.id?.getSessionToken) {
                    const sessionToken = await window.shopify.id.getSessionToken();
                    headers['Authorization'] = `Bearer ${sessionToken}`;
                }
            }
        } else if (window.shopify?.id?.getSessionToken) {
            const sessionToken = await window.shopify.id.getSessionToken();
            headers['Authorization'] = `Bearer ${sessionToken}`;
        }
        return headers;
    }, []);

    // Check connection status
    const checkConnectionStatus = useCallback(async (isPolling = false) => {
        if (!shop) return;

        if (!isPolling) {
            setConnectionState(prev => {
                if (prev.status === 'setting_up') return prev;
                return { ...prev, status: 'loading' };
            });
        }

        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`/api/connection-status?shop=${encodeURIComponent(shop)}`, { headers });
            if (!response.ok) throw new Error(`Failed to fetch connection status: ${response.status}`);

            const data = await response.json();

            if (data.success) {
                const hasActiveConnections = data.connections?.some(
                    (c: ConnectedAccount) => c.isConnected
                );
                const hasSyncing = data.connections?.some(
                    (c: ConnectedAccount) => c.status === 'syncing'
                );
                const allError = data.connections?.length > 0 &&
                    data.connections.every((c: ConnectedAccount) => c.status === 'error');

                let newStatus: AppStatus;
                if (hasActiveConnections) {
                    newStatus = hasSyncing ? 'syncing' : 'connected';
                } else if (allError) {
                    newStatus = 'error';
                } else if (data.connections?.length > 0) {
                    // Has connections but none are active (e.g., all disconnected)
                    newStatus = 'not_connected';
                } else {
                    newStatus = 'not_connected';
                }

                // Find the primary connection's email
                const primaryConnection = data.connections?.[0];

                setConnectionState(prev => {
                    // Don't override setting_up status with not_connected from polling
                    if (prev.status === 'setting_up' && newStatus === 'not_connected') {
                        return prev;
                    }
                    return {
                        status: newStatus,
                        connections: data.connections || [],
                        shopName: data.shopName,
                        shopEmail: data.shopEmail || undefined,
                        accountEmail: primaryConnection?.userEmail,
                        setupEmailExists: data.setupEmailExists,
                    };
                });
            }
        } catch (error) {
            console.error('Error checking connection status:', error);
            if (!isPolling) {
                setConnectionState(prev => ({
                    ...prev,
                    status: 'not_connected',
                    error: t("app.errors.connectionCheckFailed")
                }));
            }
        }
    }, [shop, t, getAuthHeaders]);

    // Auto-setup: trigger connection when not connected
    const triggerAutoSetup = useCallback(async () => {
        if (isSettingUp || hasAutoSetupRun.current) return;
        hasAutoSetupRun.current = true;
        setIsSettingUp(true);
        setSetupError(null);

        settingUpSince.current = Date.now();
        setConnectionState(prev => ({ ...prev, status: 'setting_up' }));

        try {
            const headers = await getAuthHeaders();
            headers['Content-Type'] = 'application/json';

            const response = await fetch('/api/auto-setup', {
                method: 'POST',
                headers,
                body: JSON.stringify({}),
                signal: AbortSignal.timeout(30000)
            });

            const data = await response.json();

            if (data.success) {
                // Poll for status update
                setTimeout(() => checkConnectionStatus(false), 2000);
            } else {
                console.error('[Auto-Setup] Failed:', data.error);
                setSetupError(data.error || t("app.errors.setupFailed"));
                settingUpSince.current = null;
                setConnectionState(prev => ({
                    ...prev,
                    status: 'error',
                    error: data.error
                }));
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : t("app.errors.setupFailed");
            console.error('[Auto-Setup] Error:', error);
            setSetupError(message);
            settingUpSince.current = null;
            setConnectionState(prev => ({
                ...prev,
                status: 'error',
                error: message
            }));
        } finally {
            setIsSettingUp(false);
        }
    }, [isSettingUp, getAuthHeaders, checkConnectionStatus]);

    // Wait for App Bridge to be ready before allowing any API calls
    useEffect(() => {
        waitForAppBridge().then(() => setAppBridgeReady(true));
    }, []);

    // ─── Shopify Billing state ──────────────────────────────────────────
    // billingState drives whether we render <PlanPicker /> or the existing UI.
    // Loaded once on first mount AFTER App Bridge is ready, and re-loaded on:
    //   - return from Shopify confirmation URL (URL has billing_return=1)
    //   - successful OTP link (so post-link we can call /activate)
    const [billingState, setBillingState] = useState<{
        loaded: boolean;
        accountExists: boolean;
        accountId: string | null;
        accountEmail: string | null;
        billingSource: string | null;
        billingStatus: string | null;
        liveStatus: string | null;
        shopifySubscriptionId: string | null;
        isBillingShop: boolean;
        currentPlanKey: string | null;
        previousPlanKey: string | null;
        hasActiveSubscription: boolean;
        hasActiveWebsiteSubscription: boolean;
    }>({
        loaded: false,
        accountExists: false,
        accountId: null,
        accountEmail: null,
        billingSource: null,
        billingStatus: null,
        liveStatus: null,
        shopifySubscriptionId: null,
        isBillingShop: false,
        currentPlanKey: null,
        previousPlanKey: null,
        hasActiveSubscription: false,
        hasActiveWebsiteSubscription: false,
    });
    // True when the merchant clicks "Change plan" on the connected screen —
    // forces the PlanPicker to mount even though they're already linked.
    const [showPlanPickerOverride, setShowPlanPickerOverride] = useState(false);
    const [billingActivating, setBillingActivating] = useState(false);
    const [billingError, setBillingError] = useState<string | null>(null);
    // Success confirmation shown when we detect the account is billed via the
    // website (Stripe) — e.g. the merchant subscribed on dashbal.com while the
    // plan picker sat open in another tab. The picker closes itself once the
    // fresh status lands (hasActiveSubscription gates it); this notice explains
    // WHY it closed, so the merchant feels confirmed rather than confused.
    const [websiteBillingNotice, setWebsiteBillingNotice] = useState(false);
    // Tracks the previous loaded value so the notice only fires on an actual
    // false→true transition within this session (a fresh mount that loads
    // straight into website billing shows the Settings note instead).
    const prevWebsiteSubRef = useRef<boolean | null>(null);

    const freeTierStorageKey = shop ? `dashbal_free_tier_${shop}` : null;
    const freePlanKeyStorageKey = shop ? `dashbal_free_plan_${shop}` : null;
    const [freeTierAccepted, setFreeTierAccepted] = useState<boolean>(() => {
        if (typeof window === 'undefined' || !freeTierStorageKey) return false;
        return window.localStorage.getItem(freeTierStorageKey) === '1';
    });
    const [freePlanKey, setFreePlanKey] = useState<string | null>(() => {
        if (typeof window === 'undefined' || !freePlanKeyStorageKey) return null;
        return window.localStorage.getItem(freePlanKeyStorageKey);
    });
    // Guard against double-activation when state updates trigger the effect twice.
    const freeActivationFiredRef = useRef(false);

    // Open dashbal.com with the merchant already authenticated. Mints a
    // short-lived (60s) JWT via /api/billing/sso-token, then top-level-opens
    // the consume URL the backend hands back. Falls back to the plain marketing
    // URL (logged-out) if SSO fails or the merchant isn't linked yet — that
    // way the button still "works" rather than doing nothing.
    const openDashboardWithSso = useCallback(async () => {
        const fallback = () => window.open(dashbalUrl, '_blank', 'noopener,noreferrer');
        if (!billingState.accountId) {
            fallback();
            return;
        }
        try {
            const headers = await getAuthHeaders();
            headers['Content-Type'] = 'application/json';
            const res = await fetch('/api/billing/sso-token', {
                method: 'POST',
                headers,
                body: JSON.stringify({ userId: billingState.accountId }),
            });
            const body = await res.json();
            const redirectUrl = body?.data?.redirectUrl;
            if (!res.ok || !body.success || !redirectUrl) {
                fallback();
                return;
            }
            window.open(redirectUrl, '_blank', 'noopener,noreferrer');
        } catch {
            fallback();
        }
    }, [billingState.accountId, dashbalUrl, getAuthHeaders]);

    const handleContinueFree = useCallback((plan: { key: string }) => {
        if (freeTierStorageKey) {
            try {
                window.localStorage.setItem(freeTierStorageKey, '1');
            } catch {
                // localStorage may be blocked in some browser contexts; in-memory state still works for this session.
            }
        }
        if (freePlanKeyStorageKey && plan?.key) {
            try {
                window.localStorage.setItem(freePlanKeyStorageKey, plan.key);
            } catch { /* same as above */ }
        }
        if (plan?.key) setFreePlanKey(plan.key);
        setFreeTierAccepted(true);
    }, [freeTierStorageKey, freePlanKeyStorageKey]);

    // Invoked by the PlanPicker when its pre-click freshness check (or a 409
    // from the backend) reveals the account is already billed via the website.
    // The refreshed status closes the first-run picker on its own; this also
    // dismisses the explicit "Change plan" override and raises the notice.
    const handleWebsiteBillingDetected = useCallback(() => {
        setShowPlanPickerOverride(false);
        setWebsiteBillingNotice(true);
    }, []);

    const fetchBillingStatus = useCallback(async () => {
        try {
            const headers = await getAuthHeaders();
            const res = await fetch('/api/billing/status', { headers });
            // Guard against SDK reauth bouncing us to /auth?shop=undefined,
            // which returns plaintext "No shop provided" with a 500. Parsing
            // that as JSON throws SyntaxError and crashes the polling loop.
            // Treat any non-OK or non-JSON response as a transient miss and
            // let the caller's polling retry.
            const contentType = res.headers.get('content-type') || '';
            if (!res.ok || !contentType.includes('application/json')) {
                console.warn('[Billing] status non-json response', res.status, contentType);
                setBillingState((p) => ({ ...p, loaded: true }));
                return null;
            }
            const body = await res.json();
            if (res.ok && body.success) {
                setBillingState({
                    loaded: true,
                    accountExists: !!body.data.accountExists,
                    accountId: body.data.accountId,
                    accountEmail: body.data.accountEmail,
                    billingSource: body.data.billingSource,
                    billingStatus: body.data.billingStatus,
                    liveStatus: body.data.liveShopifySubscription?.status || null,
                    shopifySubscriptionId: body.data.shopifySubscriptionId,
                    isBillingShop: !!body.data.isBillingShop,
                    currentPlanKey: body.data.currentPlanKey || null,
                    previousPlanKey: body.data.previousPlanKey || null,
                    hasActiveSubscription: !!body.data.hasActiveSubscription,
                    hasActiveWebsiteSubscription: !!body.data.hasActiveWebsiteSubscription,
                });
                return body.data;
            }
        } catch (err) {
            console.warn('[Billing] status fetch failed', err);
        }
        setBillingState((p) => ({ ...p, loaded: true }));
        return null;
    }, [getAuthHeaders]);

    // Initial billing status check
    useEffect(() => {
        if (appBridgeReady && shop && !billingState.loaded) {
            fetchBillingStatus();
        }
    }, [appBridgeReady, shop, billingState.loaded, fetchBillingStatus]);

    // Raise the "you're all set" notice when website billing appears mid-
    // session (false→true on a loaded snapshot) — the merchant subscribed on
    // dashbal.com in another tab and the picker is about to close under them.
    useEffect(() => {
        if (!billingState.loaded) return;
        const prev = prevWebsiteSubRef.current;
        prevWebsiteSubRef.current = billingState.hasActiveWebsiteSubscription;
        if (prev === false && billingState.hasActiveWebsiteSubscription) {
            setWebsiteBillingNotice(true);
        }
    }, [billingState.loaded, billingState.hasActiveWebsiteSubscription]);

    // Re-pull billing status once the shop becomes linked (connected/syncing) so
    // the picker gate re-evaluates with the now-known account. Without this, a
    // merchant who links via OTP during this session keeps the pre-link snapshot
    // (accountExists:false) and the picker decision would be stale.
    useEffect(() => {
        if (
            appBridgeReady &&
            (connectionState.status === 'connected' || connectionState.status === 'syncing')
        ) {
            fetchBillingStatus();
        }
    }, [appBridgeReady, connectionState.status, fetchBillingStatus]);

    // Return-from-confirmation polling — when URL has ?billing_return=1, poll
    // /api/billing/status up to ~30s waiting for liveStatus=ACTIVE (EC-28).
    // Extracted into a callback so the error banner's Retry can re-run the
    // poll after a timeout (Shopify can be slow to report the new sub).
    const billingPollCancelRef = useRef(false);
    const lastBillingPlanKeyRef = useRef<string | null>(null);

    const runBillingReturnPolling = useCallback((planKey: string | null) => {
        lastBillingPlanKeyRef.current = planKey;
        let attempts = 0;
        const MAX_ATTEMPTS = 15;
        const POLL_INTERVAL_MS = 2000;

        const tick = async () => {
            if (billingPollCancelRef.current) return;
            attempts++;
            const data = await fetchBillingStatus();

            if (data?.liveShopifySubscription?.status === 'ACTIVE') {
                // Live sub is ACTIVE. If we already have a linked Dashbal account,
                // call /activate to provision the Subscription row. Otherwise the
                // OTP linking flow below will handle it after the user links.
                if (data.accountId && planKey && data.liveShopifySubscription?.id) {
                    setBillingActivating(true);
                    try {
                        const headers = await getAuthHeaders();
                        headers['Content-Type'] = 'application/json';
                        await fetch('/api/billing/activate', {
                            method: 'POST',
                            headers,
                            body: JSON.stringify({
                                userId: data.accountId,
                                shopifySubscriptionId: data.liveShopifySubscription.id,
                                planKey,
                                shopCountry: 'US',
                            }),
                        });
                        await fetchBillingStatus();
                    } catch (err) {
                        setBillingError(t('billing.activationFailed'));
                    } finally {
                        setBillingActivating(false);
                    }
                }
                // Clean URL so refresh doesn't re-poll
                window.history.replaceState({}, '', window.location.pathname);
                return;
            }

            if (attempts >= MAX_ATTEMPTS) {
                setBillingError(t('billing.activationFailed'));
                window.history.replaceState({}, '', window.location.pathname);
                return;
            }

            setTimeout(tick, POLL_INTERVAL_MS);
        };

        tick();
    }, [fetchBillingStatus, getAuthHeaders, t]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (!params.get('billing_return')) return;

        billingPollCancelRef.current = false;
        runBillingReturnPolling(params.get('plan'));
        return () => {
            billingPollCancelRef.current = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Retry after a polling timeout: clears the error and re-polls. Also
    // covers a failed free activation — the status refetch re-arms the
    // free-activation effect (freeActivationFiredRef is reset on failure).
    const retryBillingActivation = useCallback(() => {
        setBillingError(null);
        billingPollCancelRef.current = false;
        runBillingReturnPolling(lastBillingPlanKeyRef.current);
    }, [runBillingReturnPolling]);

    // Free-tier activation: after the merchant has clicked "Continue with Free"
    // (freeTierAccepted) AND completed OTP linking (billingState.accountId is
    // set) AND isn't already on a Shopify-billed plan, provision the Subscription
    // row on the website backend. Free has no Shopify confirmation URL flow, so
    // this effect is the equivalent of the billing-return polling above.
    useEffect(() => {
        if (!freeTierAccepted) return;
        if (!billingState.loaded) return;
        if (!billingState.accountId) return;
        if (freeActivationFiredRef.current) return;
        // Skip ONLY if the merchant is already actively provisioned on the
        // exact free plan they picked. Required guard parts:
        //   - billingStatus === 'active' → covers uninstall/reinstall: after
        //     an uninstall the user is in 'grace_period' even though
        //     billingSource is still 'shopify', so we MUST re-fire activation
        //     to bring them out of grace
        //   - currentPlanKey === freePlanKey → covers Paid → Free downgrade:
        //     billingSource is 'shopify' but the active plan differs, so
        //     re-fire to reprovision under the free plan key
        if (
            billingState.billingSource === 'shopify' &&
            billingState.billingStatus === 'active' &&
            billingState.currentPlanKey &&
            (!freePlanKey || billingState.currentPlanKey === freePlanKey)
        ) {
            return;
        }

        const resolveAndActivate = async () => {
            let planKey = freePlanKey;
            if (!planKey) {
                // Fallback for sessions where the planKey wasn't persisted
                // (older localStorage state): fetch the plan catalog and pick
                // the first free plan.
                try {
                    const headers = await getAuthHeaders();
                    const r = await fetch('/api/billing/plans?shopCountry=US', { headers });
                    const j = await r.json();
                    const freePlan = (j?.data || []).find((p: { isFree?: boolean }) => p.isFree);
                    planKey = freePlan?.key || null;
                } catch { /* fall through, will surface error below */ }
            }
            if (!planKey) {
                setBillingError(t('billing.activationFailed'));
                return;
            }
            freeActivationFiredRef.current = true;
            setBillingActivating(true);
            try {
                const headers = await getAuthHeaders();
                headers['Content-Type'] = 'application/json';
                const res = await fetch('/api/billing/activate-free', {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        userId: billingState.accountId,
                        planKey,
                    }),
                });
                const body = await res.json();
                if (!res.ok || !body.success) {
                    if (body.code === 'BILLING_MANAGED_BY_WEBSITE') {
                        // The account is already covered by a website (Stripe)
                        // subscription — the free tier isn't needed. Drop the
                        // persisted free intent so this never re-fires, and
                        // confirm instead of erroring.
                        if (freeTierStorageKey) {
                            try { window.localStorage.removeItem(freeTierStorageKey); } catch { /* ignore */ }
                        }
                        if (freePlanKeyStorageKey) {
                            try { window.localStorage.removeItem(freePlanKeyStorageKey); } catch { /* ignore */ }
                        }
                        setFreeTierAccepted(false);
                        setFreePlanKey(null);
                        setWebsiteBillingNotice(true);
                        await fetchBillingStatus();
                        return;
                    }
                    throw new Error(body.error || `HTTP ${res.status}`);
                }
                await fetchBillingStatus();
            } catch (err) {
                console.warn('[Billing] free activation failed', err);
                freeActivationFiredRef.current = false;
                setBillingError(t('billing.activationFailed'));
            } finally {
                setBillingActivating(false);
            }
        };

        resolveAndActivate();
    }, [
        freeTierAccepted,
        billingState.loaded,
        billingState.accountId,
        billingState.billingSource,
        billingState.billingStatus,
        billingState.currentPlanKey,
        freePlanKey,
        freeTierStorageKey,
        freePlanKeyStorageKey,
        getAuthHeaders,
        fetchBillingStatus,
        t,
    ]);
    // ────────────────────────────────────────────────────────────────────

    // Initial status check
    useEffect(() => {
        const init = async () => {
            if (shop && !hasInitialCheckRun.current) {
                hasInitialCheckRun.current = true;
                await checkConnectionStatus(false);
            }
        };
        init();
    }, [shop, checkConnectionStatus]);

    // Poll for status when syncing or setting up (with timeout safety for setting_up)
    useEffect(() => {
        let pollInterval: NodeJS.Timeout;

        if (connectionState.status === 'syncing' || connectionState.status === 'setting_up') {
            pollInterval = setInterval(() => {
                // Safety: if setting_up has been running for over 45 seconds, transition to error
                if (connectionState.status === 'setting_up' && settingUpSince.current) {
                    const elapsed = Date.now() - settingUpSince.current;
                    if (elapsed > 45000) {
                        settingUpSince.current = null;
                        hasAutoSetupRun.current = false;
                        setSetupError(t("app.errors.setupTimeout"));
                        setConnectionState(prev => ({
                            ...prev,
                            status: 'error',
                            error: 'Connection setup timed out'
                        }));
                        return;
                    }
                }
                checkConnectionStatus(true);
            }, 5000);
        }

        return () => {
            if (pollInterval) clearInterval(pollInterval);
        };
    }, [connectionState.status, checkConnectionStatus]);

    // Refresh on focus — connection AND billing. Billing matters here: the
    // merchant may have subscribed on dashbal.com in another tab, and the plan
    // picker must dismiss itself instead of offering a second checkout.
    useEffect(() => {
        if (!shop) return;

        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                checkConnectionStatus(true);
                if (appBridgeReady) {
                    fetchBillingStatus();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [shop, checkConnectionStatus, appBridgeReady, fetchBillingStatus]);

    // Update document title
    useEffect(() => {
        document.title = t("app.title");
    }, [t]);

    // Handle retry sync
    const handleRetrySync = async () => {
        setIsRetryingSync(true);
        try {
            const headers = await getAuthHeaders();
            headers['Content-Type'] = 'application/json';

            const response = await fetch("/api/retry-sync", {
                method: "POST",
                headers
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || "Failed to trigger sync");
            }

            setConnectionState(prev => ({ ...prev, status: 'syncing' }));
            setTimeout(() => checkConnectionStatus(true), 3000);
        } catch (error: any) {
            console.error("Retry sync failed:", error);
            setSetupError(error.message);
        } finally {
            setIsRetryingSync(false);
        }
    };

    // Handle retry auto-setup
    const handleRetrySetup = () => {
        hasAutoSetupRun.current = false;
        setSetupError(null);
        triggerAutoSetup();
    };

    // Handle alternative login OTP Request
    const handleRequestOtp = async () => {
        if (!loginEmail) {
            setSetupError(t("app.errors.emailRequired"));
            return;
        }

        setIsLoggingIn(true);
        setSetupError(null);

        try {
            const headers = await getAuthHeaders();
            headers['Content-Type'] = 'application/json';

            const response = await fetch('/api/request-link-otp', {
                method: 'POST',
                headers,
                body: JSON.stringify({ email: loginEmail })
            });

            const data = await response.json();

            if (data.success) {
                setWillCreateAccount(!!data.isNewAccount);
                setIsWaitingForOtp(true);
                setSetupError(null);
            } else {
                setSetupError(data.error || t("app.errors.otpSendFailed"));
            }
        } catch (error: any) {
            setSetupError(error.message || 'Failed to request OTP');
        } finally {
            setIsLoggingIn(false);
        }
    };

    // Handle alternative login setup via OTP verification
    const handleVerifyOtp = async (codeOverride?: string) => {
        // codeOverride lets the Shopify review flow submit the static test code
        // without a manual entry step. typeof guard avoids treating a click
        // event as a code when this is used directly as an onClick handler.
        const code = typeof codeOverride === 'string' ? codeOverride : loginOtp;
        if (!loginEmail || !code) {
            setSetupError(t("app.errors.otpRequired"));
            return;
        }

        setIsLoggingIn(true);
        setSetupError(null);
        setConnectionState(prev => ({ ...prev, status: 'setting_up' }));

        try {
            const headers = await getAuthHeaders();
            headers['Content-Type'] = 'application/json';

            const response = await fetch('/api/auto-setup', {
                method: 'POST',
                headers,
                body: JSON.stringify({ email: loginEmail, otpCode: code })
            });

            const data = await response.json();

            if (data.success) {
                // Clear login state on success
                setLoginEmail('');
                setLoginOtp('');
                setIsWaitingForOtp(false);

                setTimeout(() => checkConnectionStatus(false), 2000);
            } else {
                setSetupError(data.error || t("app.errors.verifyFailed"));
                setConnectionState(prev => ({ ...prev, status: 'not_connected' }));
                // Reset to email phase so user can request a new code
                setLoginOtp('');
                setIsWaitingForOtp(false);
            }
        } catch (error: any) {
            console.error('[Login-Setup] Error:', error);
            setSetupError(error.message || 'Failed to verify code');
            setConnectionState(prev => ({ ...prev, status: 'not_connected' }));
            // Reset to email phase so user can request a new code
            setLoginOtp('');
            setIsWaitingForOtp(false);
        } finally {
            setIsLoggingIn(false);
        }
    };

    // Handle disconnect
    const handleDisconnect = async () => {
        if (!disconnectTarget) return;
        setIsDisconnecting(true);
        try {
            const headers = await getAuthHeaders();
            headers['Content-Type'] = 'application/json';

            const response = await fetch("/api/disconnect", {
                method: "POST",
                headers,
                body: JSON.stringify({ userId: disconnectTarget.userId })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || t("app.disconnect.error"));
            }

            const data = await response.json();
            if (data.success) {
                setShowDisconnectModal(false);
                setDisconnectTarget(null);
                hasAutoSetupRun.current = false; // Allow re-setup

                // Clear any lingering login state from previous custom email/OTP flows
                setLoginEmail('');
                setLoginOtp('');
                setIsWaitingForOtp(false);

                checkConnectionStatus(false);
            }
        } catch (error: any) {
            console.error("Disconnect failed:", error);
            setSetupError(error.message);
            setShowDisconnectModal(false);
        } finally {
            setIsDisconnecting(false);
        }
    };

    // Get status badge
    const getStatusBadge = (status: string) => {
        switch (status.toLowerCase()) {
            case 'active':
            case 'connected':
                return <Badge tone="success">{t("app.status.active")}</Badge>;
            case 'syncing':
                return <Badge tone="info">{t("app.status.syncing")}</Badge>;
            case 'error':
                return <Badge tone="critical">{t("app.status.error")}</Badge>;
            case 'disconnected':
                return <Badge tone="warning">{t("app.status.disconnected")}</Badge>;
            default:
                return <Badge tone="attention">{t("app.status.pending")}</Badge>;
        }
    };

    // Get the primary connection for display
    const primaryConnection = connectionState.connections.find(c => c.isConnected) ||
        connectionState.connections[0];

    // Format last sync date
    const formatLastSync = (dateStr?: string) => {
        if (!dateStr) return t("app.status.lastSyncedNever");
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return t("app.status.lastSyncedJustNow");
        if (diffMins < 60) return t("app.status.lastSyncedMinute", { count: diffMins });
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return t("app.status.lastSyncedHour", { count: diffHours });
        return date.toLocaleDateString(i18n.language);
    };

    // ─── Shopify Billing first-screen gate ─────────────────────────────
    // Determines whether to render <PlanPicker /> instead of the existing UI.
    //
    // Billing must NEVER appear before the merchant has authenticated against
    // the Dashbal account in THIS session. Two reasons:
    //   1. /billing/status is keyed by shop; before the shop is linked we can't
    //      know whether the merchant already pays (Stripe/website OR a prior
    //      Shopify sub), so the picker could re-prompt a paying customer.
    //   2. On reinstall the shopifyConnection row PERSISTS (uninstall only sets
    //      isActive:false), so accountExists alone is true even though nobody
    //      re-authenticated. Shopify OAuth re-proves STORE control, not Dashbal
    //      ACCOUNT control — the store may have changed hands — so surfacing the
    //      prior account's plan/data without OTP would leak it to a new owner.
    //
    // So we require an ACTIVE link this session: connectionState connected/
    // syncing, which is only reached after a successful OTP re-link (auto-connect
    // needs the OTP-derived account id; a persisted-but-uninstalled channel reads
    // as not_connected). Until then the connect/OTP screen renders (below this
    // early return). Once linked, show the picker only if the account has NO
    // active subscription of any source — covering a brand-new account AND a
    // Shopify account dropped to grace after uninstall (its sub is past_due, so
    // it doesn't count; previousPlanKey then pre-highlights the prior plan for a
    // one-tap resubscribe). The liveStatus guard avoids flashing the picker
    // during the activation window.
    const accountLinkedThisSession =
        connectionState.status === 'connected' || connectionState.status === 'syncing';
    const showPlanPickerFirstRun =
        billingState.loaded &&
        !billingActivating &&
        !freeTierAccepted &&
        accountLinkedThisSession &&
        billingState.accountExists &&
        !billingState.hasActiveSubscription &&
        billingState.liveStatus !== 'ACTIVE';
    const showPlanPicker = showPlanPickerFirstRun || showPlanPickerOverride;

    if (showPlanPicker) {
        return (
            <AppProvider i18n={i18n.language.startsWith('fr') ? frTranslations : enTranslations}>
                {billingError && (
                    <Box padding="400">
                        <Banner
                            tone="critical"
                            onDismiss={() => setBillingError(null)}
                            action={{
                                content: t('billing.retry'),
                                onAction: retryBillingActivation,
                            }}
                        >
                            {billingError}
                        </Banner>
                    </Box>
                )}
                <PlanPicker
                    shop={shop}
                    shopCountry="US"
                    getAuthHeaders={getAuthHeaders}
                    onContinueFree={handleContinueFree}
                    recheckBillingStatus={fetchBillingStatus}
                    onWebsiteBillingDetected={handleWebsiteBillingDetected}
                    currentPlanKey={billingState.currentPlanKey}
                    previousPlanKey={billingState.previousPlanKey}
                    onCancel={showPlanPickerOverride ? () => setShowPlanPickerOverride(false) : undefined}
                />
            </AppProvider>
        );
    }

    if (billingActivating) {
        return (
            <AppProvider i18n={i18n.language.startsWith('fr') ? frTranslations : enTranslations}>
                <Page>
                    <Card>
                        <Box padding="800">
                            <BlockStack gap="400" inlineAlign="center">
                                <Spinner size="large" />
                                <Text as="p" alignment="center">{t('billing.activating')}</Text>
                            </BlockStack>
                        </Box>
                    </Card>
                </Page>
            </AppProvider>
        );
    }
    // ──────────────────────────────────────────────────────────────────

    return (
        <AppProvider i18n={i18n.language.startsWith('fr') ? frTranslations : enTranslations}>
            <Page>
                {/* Error Banner */}
                {setupError && (
                    <Box paddingBlockEnd="400">
                        <Banner tone="critical" onDismiss={() => setSetupError(null)}>
                            {setupError}
                        </Banner>
                    </Box>
                )}

                {/* Website-billing confirmation — shown when the merchant's
                    subscription turned out to be handled on dashbal.com, so the
                    plan picker stepped aside. Informational only: no link to
                    external checkout (Shopify Billing policy). */}
                {websiteBillingNotice && (
                    <Box paddingBlockEnd="400">
                        <Banner
                            tone="success"
                            title={t("billing.websiteSubscriptionTitle")}
                            onDismiss={() => setWebsiteBillingNotice(false)}
                        >
                            {t("billing.websiteSubscriptionActive")}
                        </Banner>
                    </Box>
                )}

                <Layout>
                    {/* Main Content */}
                    <Layout.Section>
                        {/* Loading State */}
                        {connectionState.status === 'loading' && (
                            <Card>
                                <Box padding="800">
                                    <BlockStack gap="400" inlineAlign="center">
                                        <Spinner size="large" />
                                        <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                                            {t("app.status.checking")}
                                        </Text>
                                    </BlockStack>
                                </Box>
                            </Card>
                        )}

                        {/* Setting Up State */}
                        {connectionState.status === 'setting_up' && (
                            <Card>
                                <Box padding="600">
                                    <BlockStack gap="400" inlineAlign="center">
                                        <Spinner size="large" />
                                        <Text as="h2" variant="headingMd" alignment="center">
                                            {t("app.status.settingUpTitle")}
                                        </Text>
                                        <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                                            {t("app.status.settingUpDescription")}
                                        </Text>
                                        <Box paddingBlockStart="200" width="100%">
                                            <ProgressBar progress={75} tone="primary" size="small" />
                                        </Box>
                                    </BlockStack>
                                </Box>
                            </Card>
                        )}

                        {/* Connected / Syncing State */}
                        {(connectionState.status === 'connected' || connectionState.status === 'syncing') && primaryConnection && (
                            <BlockStack gap="400">
                                {/* Status Overview */}
                                <Card>
                                    <BlockStack gap="400">
                                        <InlineStack align="space-between" blockAlign="center">
                                            <BlockStack gap="100">
                                                <Text as="h2" variant="headingMd">
                                                    {t("app.connectionStatus.title")}
                                                </Text>
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    {t("app.connectionStatus.store")} <strong>{connectionState.shopName || shop}</strong>
                                                </Text>
                                            </BlockStack>
                                            {getStatusBadge(primaryConnection.status)}
                                        </InlineStack>

                                        <Divider />

                                        <InlineStack align="space-between" blockAlign="center" wrap={true} gap="400">
                                            <BlockStack gap="100">
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    {t("app.status.dashbalAccount")}
                                                </Text>
                                                <Text as="p" variant="bodyMd" fontWeight="semibold">
                                                    {primaryConnection.userEmail || t("app.common.unknown")}
                                                </Text>
                                            </BlockStack>
                                        </InlineStack>
                                    </BlockStack>
                                </Card>

                                {/* What You Can Do */}
                                <Card>
                                    <BlockStack gap="300">
                                        <Text as="h2" variant="headingMd">
                                            {t("app.whatNext.title")}
                                        </Text>
                                        <Text as="p" variant="bodyMd" tone="subdued">
                                            {t("app.whatNext.description")}
                                        </Text>
                                        <InlineStack gap="300" wrap={true}>
                                            <Button
                                                onClick={openDashboardWithSso}
                                                variant="primary"
                                            >
                                                {t("app.actions.openDashboard")}
                                            </Button>
                                            {primaryConnection.status === 'error' && primaryConnection.syncDetails?.isRetryable && (
                                                <Button
                                                    onClick={handleRetrySync}
                                                    loading={isRetryingSync}
                                                    variant="secondary"
                                                >
                                                    {t("app.actions.retrySync")}
                                                </Button>
                                            )}
                                        </InlineStack>
                                    </BlockStack>
                                </Card>
                            </BlockStack>
                        )}

                        {/* Error State (no connections at all) */}
                        {connectionState.status === 'error' && !primaryConnection && (
                            <Card>
                                <EmptyState
                                    heading={t("app.status.errorTitle")}
                                    action={{
                                        content: t("app.actions.retrySetup"),
                                        onAction: handleRetrySetup,
                                        loading: isSettingUp
                                    }}
                                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                                >
                                    <p>
                                        {setupError || (
                                            <Text as="p" variant="bodyMd" tone="subdued">
                                                {t("app.status.errorDescription")} <Link url={contactSupportUrl || `mailto:${supportEmail}`} target={contactSupportUrl ? "_blank" : undefined}>{t("app.status.contactSupport")}</Link>.
                                            </Text>
                                        )}
                                    </p>
                                </EmptyState>
                            </Card>
                        )}

                        {/* Not Connected State (Requires user action to connect) */}
                        {connectionState.status === 'not_connected' && !isSettingUp && (
                            <Card>
                                <BlockStack gap="400">
                                    <Text as="h2" variant="headingMd">
                                        {t("app.notConnected.title")}
                                    </Text>
                                    
                                    <Text as="p" variant="bodyMd" tone="subdued">
                                        {isWaitingForOtp
                                            ? (willCreateAccount
                                                ? t("app.notConnected.checkEmailNewAccount")
                                                : t("app.notConnected.checkEmail"))
                                            : t("app.notConnected.enterEmail")}
                                    </Text>

                                    <Box paddingBlockEnd="200">
                                        <BlockStack gap="300">
                                            <TextField
                                                label={t("app.notConnected.emailLabel")}
                                                value={loginEmail}
                                                onChange={setLoginEmail}
                                                autoComplete="email"
                                                type="email"
                                                disabled={isWaitingForOtp}
                                                helpText={!isWaitingForOtp ? t("app.notConnected.emailHelp") : undefined}
                                                prefix={<Icon source={EmailIcon} tone="base" />}
                                            />

                                            {isWaitingForOtp && (
                                                <TextField
                                                    label={t("app.notConnected.otpLabel")}
                                                    value={loginOtp}
                                                    onChange={setLoginOtp}
                                                    autoComplete="one-time-code"
                                                    maxLength={6}
                                                    helpText={t("app.notConnected.otpHelp")}
                                                />
                                            )}
                                        </BlockStack>
                                    </Box>

                                    <InlineStack align="start" gap="300">
                                        {isWaitingForOtp ? (
                                            <Button
                                                variant="primary"
                                                loading={isLoggingIn}
                                                onClick={() => handleVerifyOtp()}
                                                disabled={!isValidEmail || loginOtp.length < 6}
                                            >
                                                {t("app.notConnected.verifyButton")}
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="primary"
                                                loading={isLoggingIn || isSettingUp}
                                                onClick={handleRequestOtp}
                                                disabled={!isValidEmail}
                                            >
                                                {connectionState.setupEmailExists ? t("app.notConnected.linkButton") : t("app.notConnected.requestButton")}
                                            </Button>
                                        )}

                                        {isWaitingForOtp && (
                                            <Button
                                                variant="plain"
                                                disabled={isLoggingIn || isSettingUp}
                                                onClick={() => {
                                                    setIsWaitingForOtp(false);
                                                    setLoginOtp('');
                                                    setSetupError(null);
                                                    setWillCreateAccount(false);
                                                }}
                                            >
                                                {t("app.notConnected.cancelButton")}
                                            </Button>
                                        )}
                                    </InlineStack>

                                    {setupError && (
                                        <Banner tone="critical">
                                            {setupError}
                                        </Banner>
                                    )}
                                </BlockStack>
                            </Card>
                        )}
                    </Layout.Section>

                    {/* Sidebar */}
                    {(connectionState.status === 'connected' || connectionState.status === 'syncing') && (
                        <Layout.Section variant="oneThird">
                            <BlockStack gap="400">
                                {/* Quick Links */}
                                <Card>
                                    <BlockStack gap="300">
                                        <Text as="h2" variant="headingMd">
                                            {t("app.quickLinks.title")}
                                        </Text>
                                        <List>
                                            <List.Item>
                                                <Link
                                                    onClick={() => {
                                                        openDashboardWithSso();
                                                    }}
                                                >
                                                    {t("app.actions.openDashboard")}
                                                </Link>
                                            </List.Item>
                                            {docsUrl && (
                                                <List.Item>
                                                    <Link url={docsUrl} target="_blank">
                                                        {t("app.actions.documentation")}
                                                    </Link>
                                                </List.Item>
                                            )}
                                            {(contactSupportUrl || supportEmail) && (
                                                <List.Item>
                                                    <Link
                                                        url={contactSupportUrl || `mailto:${supportEmail}`}
                                                        target={contactSupportUrl ? "_blank" : undefined}
                                                    >
                                                        {t("app.support.contactSupport")}
                                                    </Link>
                                                </List.Item>
                                            )}
                                        </List>
                                    </BlockStack>
                                </Card>

                                {/* Settings */}
                                <Card>
                                    <BlockStack gap="300">
                                        <Text as="h2" variant="headingMd">
                                            {t("app.settings.title")}
                                        </Text>
                                        <Text as="p" variant="bodySm" tone="subdued">
                                            {t("app.settings.description")}
                                        </Text>
                                        {billingState.billingSource === 'stripe' && billingState.hasActiveSubscription ? (
                                            // Billed via the website (Stripe): never offer a Shopify plan
                                            // change here — creating a Shopify sub alongside the active
                                            // Stripe one would be mixed billing. Keep the app Shopify-only
                                            // by surfacing no in-app billing action, just a note.
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                {t("app.settings.billedViaWebsite", "Billing is managed on your Dashbal account.")}
                                            </Text>
                                        ) : (
                                            <Button
                                                variant="plain"
                                                onClick={() => {
                                                    // Clear the persisted free-tier intent so the picker doesn't
                                                    // auto-resolve to Free on the first re-render. The activation
                                                    // effect's ref is reset so a subsequent Free pick can fire.
                                                    if (freeTierStorageKey) {
                                                        try { window.localStorage.removeItem(freeTierStorageKey); } catch { /* ignore */ }
                                                    }
                                                    if (freePlanKeyStorageKey) {
                                                        try { window.localStorage.removeItem(freePlanKeyStorageKey); } catch { /* ignore */ }
                                                    }
                                                    setFreeTierAccepted(false);
                                                    setFreePlanKey(null);
                                                    freeActivationFiredRef.current = false;
                                                    setShowPlanPickerOverride(true);
                                                }}
                                            >
                                                {t("app.actions.changePlan")}
                                            </Button>
                                        )}
                                        <Button
                                            tone="critical"
                                            variant="plain"
                                            onClick={() => {
                                                if (primaryConnection) {
                                                    setDisconnectTarget(primaryConnection);
                                                    setShowDisconnectModal(true);
                                                }
                                            }}
                                        >
                                            {t("app.actions.disconnect")}
                                        </Button>
                                    </BlockStack>
                                </Card>
                            </BlockStack>
                        </Layout.Section>
                    )}

                    {/* Sidebar for setting_up / not_connected */}
                    {(connectionState.status === 'setting_up' || connectionState.status === 'loading') && (
                        <Layout.Section variant="oneThird">
                            <Card>
                                <BlockStack gap="200">
                                    <Text as="h2" variant="headingMd">
                                        {t("app.setup.title")}
                                    </Text>
                                    <List type="number">
                                        <List.Item>{t("app.setup.step1")}</List.Item>
                                        <List.Item>{t("app.setup.step2")}</List.Item>
                                        <List.Item>{t("app.setup.step3")}</List.Item>
                                    </List>
                                </BlockStack>
                            </Card>
                        </Layout.Section>
                    )}
                </Layout>

                {/* Disconnect Confirmation Modal */}
                {showDisconnectModal && disconnectTarget && (
                    <Modal
                        open={showDisconnectModal}
                        onClose={() => {
                            setShowDisconnectModal(false);
                            setDisconnectTarget(null);
                        }}
                        title={t("app.disconnect.title")}
                        primaryAction={{
                            content: t("app.disconnect.confirm"),
                            destructive: true,
                            onAction: handleDisconnect,
                            loading: isDisconnecting
                        }}
                        secondaryActions={[
                            {
                                content: t("app.disconnect.cancel"),
                                onAction: () => {
                                    setShowDisconnectModal(false);
                                    setDisconnectTarget(null);
                                }
                            }
                        ]}
                    >
                        <Modal.Section>
                            <BlockStack gap="300">
                                <Text as="p">
                                    {t("app.disconnect.warning")}
                                </Text>
                                {disconnectTarget.userEmail && (
                                    <Text as="p" tone="subdued" variant="bodySm">
                                        {t("app.disconnect.accountLabel", { email: disconnectTarget.userEmail })}
                                    </Text>
                                )}
                            </BlockStack>
                        </Modal.Section>
                    </Modal>
                )}
            </Page>
        </AppProvider>
    );
}
